import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Clock, ChevronRight, Filter } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './EventsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'workshop', label: 'Workshop' },
    { id: 'palestra', label: 'Palestra' },
    { id: 'formacao', label: 'Formação' },
    { id: 'evento_social', label: 'Evento Social' },
    { id: 'outro', label: 'Outro' }
  ];

  useEffect(() => {
    loadEvents();
  }, [activeTab]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'upcoming' 
        ? `${API}/public/events/upcoming`
        : `${API}/public/events/past`;
      const response = await axios.get(endpoint);
      setEvents(response.data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('pt-PT', options);
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('pt-PT', { month: 'short' }).toUpperCase().replace('.', '')
    };
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.label : category;
  };

  const filteredEvents = selectedCategory === 'all'
    ? events
    : events.filter(e => e.category === selectedCategory);

  return (
    <div className="events-page" data-testid="events-page">
      <SEO 
        title="Agenda"
        description="Descubra os próximos eventos e atividades da AASPS. Workshops, palestras, formações e eventos sociais."
        keywords="eventos, agenda, workshops, palestras, formações, AASPS"
        url="/eventos"
      />
      <Header />

      {/* Hero Section */}
      <section className="events-hero">
        <div className="events-hero-overlay" />
        <div className="events-hero-content">
          <h1>AGENDA</h1>
          <p>Descubra os nossos próximos eventos e atividades</p>
        </div>
      </section>

      {/* Events Content */}
      <section className="events-content">
        <div className="events-container">
          
          {/* Tabs */}
          <div className="events-tabs">
            <button
              className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
              onClick={() => setActiveTab('upcoming')}
            >
              Próximos Eventos
            </button>
            <button
              className={`tab-btn ${activeTab === 'past' ? 'active' : ''}`}
              onClick={() => setActiveTab('past')}
            >
              Eventos Passados
            </button>
          </div>

          {/* Filters */}
          <div className="events-filters">
            <Filter size={18} />
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`filter-chip ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Events List */}
          {loading ? (
            <div className="loading-state">A carregar eventos...</div>
          ) : filteredEvents.length === 0 ? (
            <div className="empty-state">
              <Calendar size={48} />
              <p>
                {activeTab === 'upcoming' 
                  ? 'Não há eventos programados de momento.' 
                  : 'Não há eventos passados para mostrar.'}
              </p>
            </div>
          ) : (
            <div className="events-list">
              {filteredEvents.map(event => {
                const shortDate = formatShortDate(event.date);
                return (
                  <div key={event.id} className="event-card" data-testid={`event-${event.id}`}>
                    <div className="event-date-badge">
                      <span className="day">{shortDate.day}</span>
                      <span className="month">{shortDate.month}</span>
                    </div>
                    
                    {event.image && (
                      <div className="event-image">
                        <img src={event.image} alt={event.title} />
                        {event.is_featured && <span className="featured-badge">Destaque</span>}
                      </div>
                    )}
                    
                    <div className="event-info">
                      <span className="event-category">{getCategoryLabel(event.category)}</span>
                      <h3>{event.title}</h3>
                      
                      <div className="event-meta">
                        <div className="meta-item">
                          <Calendar size={16} />
                          <span>{formatDate(event.date)}</span>
                        </div>
                        <div className="meta-item">
                          <Clock size={16} />
                          <span>{event.time_start}{event.time_end ? ` - ${event.time_end}` : ''}</span>
                        </div>
                        <div className="meta-item">
                          <MapPin size={16} />
                          <span>{event.location}</span>
                        </div>
                      </div>
                      
                      <p className="event-description">{event.description}</p>
                      
                      {event.registration_link && activeTab === 'upcoming' && (
                        <a 
                          href={event.registration_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="register-btn"
                        >
                          Inscrever-se <ChevronRight size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
